import { PlacementType } from "@/types/inboxPlacements.types";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const SESSION_STORAGE_KEY = "runningTestData";

type TestDataItem = {
  testId: string;
  senderEmail: string;
  testType: PlacementType;
  provider: string[];
  status: "running" | "completed";
};

type PlacementTestStore = {
  isRunningPlacementOpen: boolean;
  runningTestData: TestDataItem[];
  selectedTestData: TestDataItem | null;
  setIsRunningPlacementOpen: (isOpen: boolean) => void;
  maximizeTest: (testId: string) => void;
  setSelectedTestData: (testData: TestDataItem) => void;
  addRunningTestData: (testData: TestDataItem) => void;
  removeRunningTestData: (testId: string) => void;
  updateRunningTestData: (
    testId: string,
    testData: Partial<TestDataItem>
  ) => void;
  bulkUpdateRunningTestData: (testData: TestDataItem[]) => void;
  bulkUpdateTestStatuses: (completedTestIds: string[]) => void;
  clearPlacementTestStore: () => void;
};

const getInitialRunningTestData = (): TestDataItem[] => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const storedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedData) return [];

    const parsedData = JSON.parse(storedData);
    return Array.isArray(parsedData) ? parsedData : [];
  } catch (error) {
    console.error("Error parsing stored test data:", error);
    return [];
  }
};

const updateSessionStorage = (data: TestDataItem[]) => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error updating session storage:", error);
  }
};

export const usePlacementTestStore = create<PlacementTestStore>()(
  devtools((set) => ({
    isRunningPlacementOpen: false,
    setIsRunningPlacementOpen: (isOpen: boolean) =>
      set({ isRunningPlacementOpen: isOpen, selectedTestData: null }),
    selectedTestData: null,
    setSelectedTestData: (testData: TestDataItem) =>
      set({ selectedTestData: testData }),
    runningTestData: getInitialRunningTestData(),
    addRunningTestData: (testData: TestDataItem) =>
      set((state) => {
        if (!state.runningTestData) {
          const newData = [testData];
          updateSessionStorage(newData);
          return { runningTestData: newData };
        }

        const foundIndex = state.runningTestData.findIndex(
          (test) => test.testId === testData.testId
        );

        if (foundIndex !== -1) {
          const newRunningTestData = [...state.runningTestData];
          newRunningTestData[foundIndex] = { ...testData };
          updateSessionStorage(newRunningTestData);
          return { runningTestData: newRunningTestData };
        }

        const newData = [...state.runningTestData, testData];
        updateSessionStorage(newData);
        return { runningTestData: newData };
      }),
    removeRunningTestData: (testId: string) =>
      set((state) => {
        const newData =
          state.runningTestData?.filter((test) => test.testId !== testId) ?? [];
        updateSessionStorage(newData);
        return { runningTestData: newData };
      }),
    updateRunningTestData: (
      testId: string,
      updateData: Partial<TestDataItem>
    ) =>
      set((state) => {
        const newData =
          state.runningTestData?.map((test) =>
            test.testId === testId ? { ...test, ...updateData } : test
          ) ?? [];
        updateSessionStorage(newData);
        return { runningTestData: newData };
      }),
    bulkUpdateRunningTestData: (testData: TestDataItem[]) =>
      set(() => {
        updateSessionStorage(testData);
        return { runningTestData: testData };
      }),
    bulkUpdateTestStatuses: (completedTestIds: string[]) =>
      set((state) => {
        const hasChanges = state.runningTestData.some(
          (test) =>
            completedTestIds.includes(test.testId) &&
            test.status !== "completed"
        );

        if (!hasChanges) {
          return state;
        }

        const newData = state.runningTestData.map((test) =>
          completedTestIds.includes(test.testId)
            ? { ...test, status: "completed" as const }
            : test
        );

        updateSessionStorage(newData);
        return { runningTestData: newData };
      }),
    maximizeTest: (testId: string) =>
      set((state) => ({
        isRunningPlacementOpen: true,
        selectedTestData:
          state.runningTestData?.find((test) => test.testId === testId) ?? null,
      })),
    clearPlacementTestStore: () =>
      set(() => {
        updateSessionStorage([]);
        return {
          isRunningPlacementOpen: false,
          runningTestData: [],
          selectedTestData: null,
        };
      }),
  }))
);
